import { html, nothing } from "lit";
import type { ToolCard } from "../types/chat-types.ts";
import { icons } from "../icons.ts";
import { formatToolDetail, resolveToolDisplay } from "../tool-display.ts";
import { TOOL_INLINE_THRESHOLD } from "./constants.ts";
import { extractTextCached } from "./message-extract.ts";
import { isToolResultMessage } from "./message-normalizer.ts";
import { formatToolOutputForSidebar, getTruncatedPreview } from "./tool-helpers.ts";

export function extractToolCards(message: unknown): ToolCard[] {
  const m = message as Record<string, unknown>;
  const content = normalizeContent(m.content);
  const cards: ToolCard[] = [];

  for (const item of content) {
    const kind = (typeof item.type === "string" ? item.type : "").toLowerCase();
    const isToolCall =
      ["toolcall", "tool_call", "tooluse", "tool_use"].includes(kind) ||
      (typeof item.name === "string" && item.arguments != null);
    if (isToolCall) {
      cards.push({
        kind: "call",
        name: (item.name as string) ?? "tool",
        args: coerceArgs(item.arguments ?? item.args),
      });
    }
  }

  for (const item of content) {
    const kind = (typeof item.type === "string" ? item.type : "").toLowerCase();
    if (kind !== "toolresult" && kind !== "tool_result") {
      continue;
    }
    const text = extractToolText(item);
    const name = typeof item.name === "string" ? item.name : "tool";
    cards.push({ kind: "result", name, text });
  }

  if (isToolResultMessage(message) && !cards.some((card) => card.kind === "result")) {
    const name =
      (typeof m.toolName === "string" && m.toolName) ||
      (typeof m.tool_name === "string" && m.tool_name) ||
      "tool";
    const text = extractTextCached(message) ?? undefined;
    cards.push({ kind: "result", name, text });
  }

  return cards;
}

export type ToolCardActions = {
  onOpenFilePreview?: (filePath: string) => void;
  onOpenFileFolder?: (filePath: string) => void;
};

export function renderToolCardSidebar(
  card: ToolCard,
  onOpenSidebar?: (content: string) => void,
  actions?: ToolCardActions,
) {
  const display = resolveToolDisplay({ name: card.name, args: card.args });
  const detail = formatToolDetail(display);
  const hasText = Boolean(card.text?.trim());

  const canClick = Boolean(onOpenSidebar);
  const handleClick = canClick
    ? () => {
        if (hasText) {
          onOpenSidebar!(formatToolOutputForSidebar(card.text!));
          return;
        }
        const info = `## ${display.label}\n\n${
          detail ? `**Command:** \`${detail}\`\n\n` : ""
        }*No output — tool completed successfully.*`;
        onOpenSidebar!(info);
      }
    : undefined;

  const isShort = hasText && (card.text?.length ?? 0) <= TOOL_INLINE_THRESHOLD;
  const isLarge = hasText && (card.text?.length ?? 0) > 200;
  const showCollapsed = hasText && !isShort && !isLarge;
  const showInline = hasText && isShort;
  const isEmpty = !hasText || isLarge;

  const isWriteTool = ["write", "edit", "attach", "createfile", "write_file", "create_file", "savefile", "save_file"].includes(
    card.name.toLowerCase(),
  );
  const argsObj = typeof card.args === "object" && card.args !== null
    ? card.args as Record<string, unknown>
    : typeof card.args === "string"
      ? (() => { try { return JSON.parse(card.args) as Record<string, unknown>; } catch { return {}; } })()
      : {};
  const filePath = isWriteTool
    ? (argsObj.path as string | undefined) ?? (argsObj.file_path as string | undefined) ?? (argsObj.filePath as string | undefined) ?? (argsObj.filename as string | undefined)
    : undefined;
  const showFileActions = Boolean(filePath && (actions?.onOpenFilePreview || actions?.onOpenFileFolder));

  return html`
    <div
      class="chat-tool-card ${canClick ? "chat-tool-card--clickable" : ""}"
      @click=${handleClick}
      role=${canClick ? "button" : nothing}
      tabindex=${canClick ? "0" : nothing}
      @keydown=${
        canClick
          ? (e: KeyboardEvent) => {
              if (e.key !== "Enter" && e.key !== " ") {
                return;
              }
              e.preventDefault();
              handleClick?.();
            }
          : nothing
      }
    >
      <div class="chat-tool-card__header">
        <div class="chat-tool-card__title">
          <span class="chat-tool-card__icon">${icons[display.icon]}</span>
          <span>${display.label}</span>
        </div>
        ${
          canClick
            ? html`<span class="chat-tool-card__action">${hasText ? "View" : ""} ${icons.check}</span>`
            : nothing
        }
        ${isEmpty && !canClick ? html`<span class="chat-tool-card__status">${icons.check}</span>` : nothing}
      </div>
      ${detail ? html`<div class="chat-tool-card__detail">${detail}</div>` : nothing}
      ${
        showFileActions
          ? html`<div class="chat-tool-card__file-actions">
              ${actions?.onOpenFilePreview && filePath
                ? html`<button
                    class="btn btn--sm chat-tool-card__file-btn"
                    @click=${(e: Event) => { e.stopPropagation(); actions.onOpenFilePreview!(filePath!); }}
                    title="Preview file"
                  >Preview</button>`
                : nothing}
              ${actions?.onOpenFileFolder && filePath
                ? html`<button
                    class="btn btn--sm chat-tool-card__file-btn"
                    @click=${(e: Event) => { e.stopPropagation(); actions.onOpenFileFolder!(filePath!); }}
                    title="Open containing folder"
                  >Folder</button>`
                : nothing}
            </div>`
          : nothing
      }
      ${
        isEmpty
          ? html`
              <div class="chat-tool-card__status-text muted">${
                isLarge
                  ? `Completed (${(card.text?.length ?? 0).toLocaleString()} chars — click to view)`
                  : "Completed"
              }</div>
            `
          : nothing
      }
      ${
        showCollapsed
          ? html`<div class="chat-tool-card__preview mono">${getTruncatedPreview(card.text!)}</div>`
          : nothing
      }
      ${showInline ? html`<div class="chat-tool-card__inline mono">${card.text}</div>` : nothing}
    </div>
  `;
}

function normalizeContent(content: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.filter(Boolean) as Array<Record<string, unknown>>;
}

function coerceArgs(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function extractToolText(item: Record<string, unknown>): string | undefined {
  if (typeof item.text === "string") {
    return item.text;
  }
  if (typeof item.content === "string") {
    return item.content;
  }
  return undefined;
}
